import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { COUNTRIES, getProvincesForCountry, hasProvinceDropdown, normalizeCountryForSelect } from '../utils/countriesAndProvinces';
import { formatDateForHtmlInput } from '../utils/dateInput';
import './JuventusCardForm.css';

// Signature pad (same behaviour as JuventusCardForm)
const SIGNATURE_WIDTH = 400;
const SIGNATURE_HEIGHT = 150;

const SignaturePad = ({ value, onChange, disabled, clearLabel }) => {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = SIGNATURE_WIDTH;
    canvas.height = SIGNATURE_HEIGHT;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = value;
    }
  }, []);

  useEffect(() => {
    if (value) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
  }, [value]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDraw = (e) => { e.preventDefault(); if (!disabled) { isDrawing.current = true; lastPos.current = getPoint(e); } };
  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing.current || !lastPos.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    const pos = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };
  const endDraw = (e) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    isDrawing.current = false;
    lastPos.current = null;
    const canvas = canvasRef.current;
    if (canvas) onChange(canvas.toDataURL('image/png'));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
    onChange('');
  };

  return (
    <div className="signature-pad-wrap">
      <canvas
        ref={canvasRef}
        className="signature-pad"
        width={SIGNATURE_WIDTH}
        height={SIGNATURE_HEIGHT}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <button type="button" className="btn-signature-clear" onClick={clear} disabled={disabled}>
        {disabled ? '' : clearLabel}
      </button>
    </div>
  );
};

const ID_TYPES = {
  fr: ['Carte d\'identité', 'Passeport', 'Permis de conduire'],
  it: ['Carta d\'identità', 'Passaporto', 'Patente di guida'],
  en: ['Identity card', 'Passport', 'Driving licence']
};

const PASSPORT_IDENTITY_TYPES = new Set(['Passeport', 'Passaporto', 'Passport']);

function mapUserIdentityTypeToFormOption(userType, lang) {
  const options = ID_TYPES[lang] || ID_TYPES.fr;
  if (!userType) return options[0];
  if (PASSPORT_IDENTITY_TYPES.has(userType)) return options[1];
  const exact = options.find((o) => o === userType);
  if (exact) return exact;
  return options[0];
}

function civiliteToTitle(civilite) {
  if (!civilite) return 'Sig.';
  const c = String(civilite).trim().toLowerCase();
  if (c === 'mme' || c === 'madame' || c === 'sig.ra') return 'Sig.ra';
  return 'Sig.';
}

function profileToLostPatch(profile, lang) {
  if (!profile) return null;
  const bp = profile.birthPlace || {};
  const addr = profile.address || {};
  const idn = profile.identity || {};
  const birthCountryRaw = bp.country || profile.address?.country || '';
  const birthCountry = normalizeCountryForSelect(birthCountryRaw, COUNTRIES) || 'Suisse';
  const addressCountry = normalizeCountryForSelect(addr.country || '', COUNTRIES) || 'Suisse';

  let title = 'Sig.';
  if (profile.civilite != null && String(profile.civilite).trim() !== '') {
    title = civiliteToTitle(profile.civilite);
  } else if (profile.sexe === 'Femme') {
    title = 'Sig.ra';
  } else if (profile.sexe === 'Homme') {
    title = 'Sig.';
  }

  return {
    title,
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    dateOfBirth: formatDateForHtmlInput(profile.dateOfBirth),
    birthPlace: bp.city || '',
    birthCountry,
    birthProvince: bp.canton || '',
    idDocumentType: mapUserIdentityTypeToFormOption(idn.type, lang),
    idNumber: idn.number || '',
    idExpiry: formatDateForHtmlInput(idn.expiryDate),
    addressStreet: addr.street || '',
    addressStreetNumber: addr.streetNumber || '',
    addressCity: addr.city || '',
    addressPostalCode: addr.postalCode || '',
    addressProvince: addr.canton || '',
    addressCountry,
    phone: profile.phone || '',
    mobile: '',
    email: profile.email || '',
  };
}

const TRANSLATIONS = {
  fr: {
    title: 'Déclaration de perte – Juventus Card',
    intro: 'Ce formulaire permet de déclarer la perte (ou le vol) de votre Juventus Card. Les champs marqués * sont obligatoires.',
    forWhom: 'Pour qui est cette déclaration ?',
    forWhomSelf: 'Pour moi-même',
    titleLabel: 'Civilité *',
    titleMr: 'Monsieur',
    titleMrs: 'Madame',
    firstName: 'Prénom *',
    lastName: 'Nom *',
    dateOfBirth: 'Date de naissance *',
    birthPlace: 'Lieu de naissance *',
    birthCountry: 'Pays de naissance *',
    birthProvince: 'Département / canton (naissance)',
    idSection: 'Document d\'identité',
    idType: 'Type de document *',
    idNumber: 'N° du document *',
    idExpiry: 'Date d\'expiration *',
    addressSection: 'Adresse de résidence',
    addressStreet: 'Rue / Adresse *',
    addressNumber: 'N° civ. *',
    postalCode: 'Code postal *',
    city: 'Ville *',
    province: 'Département / canton (résidence)',
    country: 'Pays *',
    contactSection: 'Contact',
    phone: 'Téléphone',
    mobile: 'Portable',
    email: 'Email *',
    lossSection: 'Détails de la perte',
    cardNumber: 'N° Juventus Card (si connu)',
    dateOfLoss: 'Date de la perte / du vol *',
    placeOrCircumstances: 'Lieu ou circonstances (optionnel)',
    declaration: 'Je déclare avoir perdu ou m\'être fait voler ma Juventus Card et demande son remplacement. *',
    signaturePlace: 'Lieu de signature',
    signature: 'Signature *',
    signatureClear: 'Effacer',
    submit: 'Envoyer la déclaration',
    back: '← Retour à la page Juventus Card',
    success: 'Votre déclaration de perte a bien été enregistrée. Elle sera traitée par le club.',
    error: 'Une erreur est survenue. Veuillez réessayer.',
    lang: 'Langue du formulaire',
    selectCountry: 'Sélectionnez un pays',
    selectProvince: 'Sélectionnez...',
  },
  it: {
    title: 'Dichiarazione di smarrimento – Juventus Card',
    intro: 'Questo modulo serve a dichiarare lo smarrimento (o il furto) della Juventus Card. I campi contrassegnati * sono obbligatori.',
    forWhom: 'Per chi è questa dichiarazione?',
    forWhomSelf: 'Per me stesso/a',
    titleLabel: 'Titolo *',
    titleMr: 'Sig.',
    titleMrs: 'Sig.ra',
    firstName: 'Nome *',
    lastName: 'Cognome *',
    dateOfBirth: 'Data di nascita *',
    birthPlace: 'Luogo di nascita *',
    birthCountry: 'Stato di nascita *',
    birthProvince: 'Provincia (nascita)',
    idSection: 'Documento d\'identità',
    idType: 'Tipologia documento *',
    idNumber: 'N. documento *',
    idExpiry: 'Scadenza *',
    addressSection: 'Indirizzo di residenza',
    addressStreet: 'Indirizzo *',
    addressNumber: 'N° civ. *',
    postalCode: 'CAP *',
    city: 'Città *',
    province: 'Provincia (residenza)',
    country: 'Stato *',
    contactSection: 'Contatti',
    phone: 'Telefono',
    mobile: 'Cellulare',
    email: 'Email *',
    lossSection: 'Dettagli smarrimento',
    cardNumber: 'N. Juventus Card (se noto)',
    dateOfLoss: 'Data smarrimento/furto *',
    placeOrCircumstances: 'Luogo o circostanze (opzionale)',
    declaration: 'Dichiaro di aver smarrito o subito il furto della mia Juventus Card e ne richiedo la sostituzione. *',
    signaturePlace: 'Luogo e data',
    signature: 'Firma *',
    signatureClear: 'Cancella',
    submit: 'Invia dichiarazione',
    back: '← Torna alla pagina Juventus Card',
    success: 'La tua dichiarazione di smarrimento è stata registrata. Sarà esaminata dal club.',
    error: 'Si è verificato un errore. Riprova.',
    lang: 'Lingua del modulo',
    selectCountry: 'Seleziona uno stato',
    selectProvince: 'Selezionare...',
  },
  en: {
    title: 'Declaration of loss – Juventus Card',
    intro: 'This form is used to declare the loss (or theft) of your Juventus Card. Fields marked * are required.',
    forWhom: 'Who is this declaration for?',
    forWhomSelf: 'Myself',
    titleLabel: 'Title *',
    titleMr: 'Mr',
    titleMrs: 'Mrs',
    firstName: 'First name *',
    lastName: 'Last name *',
    dateOfBirth: 'Date of birth *',
    birthPlace: 'Place of birth *',
    birthCountry: 'Country of birth *',
    birthProvince: 'Province / canton (birth)',
    idSection: 'Identity document',
    idType: 'Document type *',
    idNumber: 'Document number *',
    idExpiry: 'Expiry date *',
    addressSection: 'Address',
    addressStreet: 'Street *',
    addressNumber: 'Number *',
    postalCode: 'Postal code *',
    city: 'City *',
    province: 'Province / canton (residence)',
    country: 'Country *',
    contactSection: 'Contact',
    phone: 'Phone',
    mobile: 'Mobile',
    email: 'Email *',
    lossSection: 'Loss details',
    cardNumber: 'Juventus Card number (if known)',
    dateOfLoss: 'Date of loss/theft *',
    placeOrCircumstances: 'Place or circumstances (optional)',
    declaration: 'I declare that I have lost or had my Juventus Card stolen and request its replacement. *',
    signaturePlace: 'Place of signature',
    signature: 'Signature *',
    signatureClear: 'Clear',
    submit: 'Submit declaration',
    back: '← Back to Juventus Card page',
    success: 'Your declaration of loss has been recorded. It will be processed by the club.',
    error: 'An error occurred. Please try again.',
    lang: 'Form language',
    selectCountry: 'Select a country',
    selectProvince: 'Select...',
  },
};

function formatDateForApi(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  return d.toISOString().split('T')[0];
}

const JuventusCardLostForm = () => {
  const location = useLocation();
  const { user: authUser } = useAuth();
  const { language: siteLang } = useLanguage();
  const juventusCardRequestId = location.state?.juventusCardRequestId || null;

  const [lang, setLang] = useState('fr');
  const [meProfile, setMeProfile] = useState(null);
  const [linkedMinors, setLinkedMinors] = useState([]);
  const [holderChoice, setHolderChoice] = useState('self');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: 'Sig.',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    birthPlace: '',
    birthCountry: 'Suisse',
    birthProvince: '',
    idDocumentType: 'Carte d\'identité',
    idNumber: '',
    idExpiry: '',
    addressStreet: '',
    addressStreetNumber: '',
    addressCity: '',
    addressPostalCode: '',
    addressProvince: '',
    addressCountry: 'Suisse',
    phone: '',
    mobile: '',
    email: '',
    juventusCardNumber: '',
    dateOfLoss: '',
    placeOrCircumstances: '',
    declarationAccepted: false,
    signaturePlace: '',
    signatureImage: '',
  });

  useEffect(() => {
    if (!authUser) {
      setMeProfile(null);
      setLinkedMinors([]);
      setHolderChoice('self');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        if (cancelled) return;
        const profile = data.user;
        if (!profile) return;
        setMeProfile(profile);
        setLinkedMinors(Array.isArray(profile.linkedMinors) ? profile.linkedMinors : []);
        const pl = profile.preferredLanguage;
        const formLang = pl === 'it' ? 'it' : 'fr';
        if (pl === 'it' || pl === 'fr') setLang(formLang);
      } catch {
        // profil non chargé
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authUser?.id, authUser?.email]);

  useEffect(() => {
    if (holderChoice === 'self') return undefined;
    if (!linkedMinors.some((m) => String(m._id) === holderChoice)) setHolderChoice('self');
    return undefined;
  }, [linkedMinors, holderChoice]);

  useEffect(() => {
    if (authUser) return undefined;
    if (siteLang === 'it' || siteLang === 'fr') setLang(siteLang);
    return undefined;
  }, [authUser, siteLang]);

  useEffect(() => {
    if (!authUser || !meProfile) return undefined;
    const formLang = lang === 'it' ? 'it' : 'fr';
    const subject =
      holderChoice === 'self'
        ? meProfile
        : linkedMinors.find((m) => String(m._id) === holderChoice);

    if (!subject) return undefined;

    const patch = profileToLostPatch(subject, formLang);
    if (!patch) return undefined;

    setFormData((prev) => ({
      ...prev,
      ...patch,
      juventusCardNumber: prev.juventusCardNumber,
      dateOfLoss: prev.dateOfLoss,
      placeOrCircumstances: prev.placeOrCircumstances,
      declarationAccepted: false,
      signaturePlace: '',
      signatureImage: '',
    }));
    return undefined;
  }, [authUser, meProfile, linkedMinors, holderChoice, lang]);

  const t = TRANSLATIONS[lang] || TRANSLATIONS.fr;
  const idTypeOptions = ID_TYPES[lang] || ID_TYPES.fr;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'birthCountry') next.birthProvince = '';
      if (name === 'addressCountry') next.addressProvince = '';
      return next;
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.declarationAccepted) {
      setError(lang === 'fr' ? 'Veuillez accepter la déclaration.' : lang === 'it' ? 'Accettare la dichiarazione.' : 'Please accept the declaration.');
      return;
    }
    if (!formData.signatureImage) {
      setError(lang === 'fr' ? 'Veuillez signer dans la zone prévue.' : lang === 'it' ? 'Si prega di firmare.' : 'Please sign in the box provided.');
      return;
    }

    const payload = {
      juventusCardRequest: juventusCardRequestId || undefined,
      title: formData.title,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      dateOfBirth: formatDateForApi(formData.dateOfBirth),
      birthPlace: formData.birthPlace.trim(),
      birthCountry: formData.birthCountry.trim(),
      birthProvince: formData.birthProvince.trim(),
      idDocumentType: formData.idDocumentType,
      idNumber: formData.idNumber.trim(),
      idExpiry: formatDateForApi(formData.idExpiry),
      addressStreet: formData.addressStreet.trim(),
      addressStreetNumber: formData.addressStreetNumber.trim(),
      addressCity: formData.addressCity.trim(),
      addressPostalCode: formData.addressPostalCode.trim(),
      addressProvince: formData.addressProvince.trim(),
      addressCountry: formData.addressCountry.trim(),
      phone: formData.phone.trim(),
      mobile: formData.mobile.trim(),
      email: formData.email.trim(),
      juventusCardNumber: formData.juventusCardNumber.trim(),
      dateOfLoss: formatDateForApi(formData.dateOfLoss),
      placeOrCircumstances: formData.placeOrCircumstances.trim(),
      declarationAccepted: true,
      signaturePlace: formData.signaturePlace.trim(),
      signatureImage: formData.signatureImage || '',
      formLanguage: lang,
    };

    if (authUser && linkedMinors.length > 0 && holderChoice !== 'self') {
      payload.lostCardHolderUser = holderChoice;
    }

    setLoading(true);
    try {
      await api.post('/juventus-card-lost', payload);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || t.error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="juventus-card-form-page">
        <div className="container">
          <h1 className="page-title">{t.title}</h1>
          <div className="juventus-card-form-success">
            <p>{t.success}</p>
            <Link to="/juventus-card" className="btn-back-link">{t.back}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="juventus-card-form-page">
      <div className="container">
        <div className="form-lang-select">
          <label>{t.lang}:</label>
          <select value={lang} onChange={(e) => setLang(e.target.value)}>
            <option value="fr">Français</option>
            <option value="it">Italiano</option>
            <option value="en">English</option>
          </select>
        </div>
        <h1 className="page-title">{t.title}</h1>
        <p className="form-intro">{t.intro}</p>

        <form onSubmit={handleSubmit} className="juventus-card-form">
          {error && <div className="form-error">{error}</div>}
          {authUser && linkedMinors.length > 0 && (
            <section className="form-section">
              <div className="form-group">
                <label htmlFor="lost-holder">{t.forWhom}</label>
                <select
                  id="lost-holder"
                  value={holderChoice}
                  onChange={(e) => {
                    setHolderChoice(e.target.value);
                    setError('');
                  }}
                >
                  <option value="self">{t.forWhomSelf}</option>
                  {linkedMinors.map((m) => (
                    <option key={String(m._id)} value={String(m._id)}>
                      {`${m.firstName || ''} ${m.lastName || ''}`.trim()}
                    </option>
                  ))}
                </select>
              </div>
            </section>
          )}

          <section className="form-section">
            <h2>{t.titleLabel}</h2>
            <div className="form-row">
              <label className="label-radio">
                <input type="radio" name="title" value="Sig." checked={formData.title === 'Sig.'} onChange={handleChange} />
                <span>{t.titleMr}</span>
              </label>
              <label className="label-radio">
                <input type="radio" name="title" value="Sig.ra" checked={formData.title === 'Sig.ra'} onChange={handleChange} />
                <span>{t.titleMrs}</span>
              </label>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">{t.firstName}</label>
                <input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">{t.lastName}</label>
                <input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dateOfBirth">{t.dateOfBirth}</label>
                <input id="dateOfBirth" name="dateOfBirth" type="date" value={formData.dateOfBirth} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="birthPlace">{t.birthPlace}</label>
                <input id="birthPlace" name="birthPlace" value={formData.birthPlace} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="birthCountry">{t.birthCountry}</label>
                <select id="birthCountry" name="birthCountry" value={formData.birthCountry} onChange={handleChange} required>
                  <option value="">{t.selectCountry}</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group form-group-small">
                <label htmlFor="birthProvince">{t.birthProvince}</label>
                {hasProvinceDropdown(formData.birthCountry) ? (
                  <select
                    id="birthProvince"
                    name="birthProvince"
                    value={formData.birthProvince}
                    onChange={handleChange}
                  >
                    <option value="">{t.selectProvince}</option>
                    {getProvincesForCountry(formData.birthCountry).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="birthProvince"
                    name="birthProvince"
                    value={formData.birthProvince}
                    onChange={handleChange}
                    placeholder="–"
                  />
                )}
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2>{t.idSection}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="idDocumentType">{t.idType}</label>
                <select id="idDocumentType" name="idDocumentType" value={formData.idDocumentType} onChange={handleChange} required>
                  {idTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="idNumber">{t.idNumber}</label>
                <input id="idNumber" name="idNumber" value={formData.idNumber} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="idExpiry">{t.idExpiry}</label>
                <input id="idExpiry" name="idExpiry" type="date" value={formData.idExpiry} onChange={handleChange} required />
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2>{t.addressSection}</h2>
            <div className="form-row">
              <div className="form-group form-group-wide">
                <label htmlFor="addressStreet">{t.addressStreet}</label>
                <input id="addressStreet" name="addressStreet" value={formData.addressStreet} onChange={handleChange} required />
              </div>
              <div className="form-group form-group-small">
                <label htmlFor="addressStreetNumber">{t.addressNumber}</label>
                <input id="addressStreetNumber" name="addressStreetNumber" value={formData.addressStreetNumber} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="addressCity">{t.city}</label>
                <input id="addressCity" name="addressCity" value={formData.addressCity} onChange={handleChange} required />
              </div>
              <div className="form-group form-group-small">
                <label htmlFor="addressPostalCode">{t.postalCode}</label>
                <input id="addressPostalCode" name="addressPostalCode" value={formData.addressPostalCode} onChange={handleChange} required />
              </div>
              <div className="form-group form-group-small">
                <label htmlFor="addressProvince">{t.province}</label>
                {hasProvinceDropdown(formData.addressCountry) ? (
                  <select
                    id="addressProvince"
                    name="addressProvince"
                    value={formData.addressProvince}
                    onChange={handleChange}
                  >
                    <option value="">{t.selectProvince}</option>
                    {getProvincesForCountry(formData.addressCountry).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="addressProvince"
                    name="addressProvince"
                    value={formData.addressProvince}
                    onChange={handleChange}
                    placeholder="–"
                  />
                )}
              </div>
              <div className="form-group">
                <label htmlFor="addressCountry">{t.country}</label>
                <select id="addressCountry" name="addressCountry" value={formData.addressCountry} onChange={handleChange} required>
                  <option value="">{t.selectCountry}</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2>{t.contactSection}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">{t.email}</label>
                <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="phone">{t.phone}</label>
                <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="mobile">{t.mobile}</label>
                <input id="mobile" name="mobile" type="tel" value={formData.mobile} onChange={handleChange} />
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2>{t.lossSection}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="juventusCardNumber">{t.cardNumber}</label>
                <input
                  id="juventusCardNumber"
                  name="juventusCardNumber"
                  type="text"
                  autoComplete="off"
                  inputMode="numeric"
                  value={formData.juventusCardNumber}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="dateOfLoss">{t.dateOfLoss}</label>
                <input id="dateOfLoss" name="dateOfLoss" type="date" value={formData.dateOfLoss} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group form-group-wide">
                <label htmlFor="placeOrCircumstances">{t.placeOrCircumstances}</label>
                <input id="placeOrCircumstances" name="placeOrCircumstances" value={formData.placeOrCircumstances} onChange={handleChange} />
              </div>
            </div>
          </section>

          <section className="form-section">
            <label className="label-checkbox">
              <input type="checkbox" name="declarationAccepted" checked={formData.declarationAccepted} onChange={handleChange} required />
              <span>{t.declaration}</span>
            </label>
            <div className="form-group">
              <label htmlFor="signaturePlace">{t.signaturePlace}</label>
              <input id="signaturePlace" name="signaturePlace" value={formData.signaturePlace} onChange={handleChange} placeholder="ex. Montreux" />
            </div>
            <div className="form-group">
              <label>{t.signature}</label>
              <SignaturePad
                value={formData.signatureImage}
                onChange={(dataUrl) => setFormData(prev => ({ ...prev, signatureImage: dataUrl }))}
                disabled={loading}
                clearLabel={t.signatureClear}
              />
            </div>
          </section>

          <div className="form-actions">
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? (lang === 'fr' ? 'Envoi...' : lang === 'it' ? 'Invio...' : 'Sending...') : t.submit}
            </button>
            <Link to="/juventus-card" className="btn-back-link">{t.back}</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JuventusCardLostForm;
